import React, { useState } from 'react';
import { Send, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { FileUploadField } from './FileUploadField';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const inputCls =
  'w-full px-4 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#7B1E2B] focus:border-transparent';

const CONTACT_FIELDS = [
  { name: 'name', label: 'Your Name *', placeholder: 'John Smith', type: 'text', testId: 'submit-name-input' },
  { name: 'email', label: 'Email *', placeholder: 'you@example.com', type: 'email', testId: 'submit-email-input' },
  { name: 'phone', label: 'Phone', placeholder: '(555) 123-4567', type: 'text', testId: 'submit-phone-input' },
  { name: 'business_name', label: 'Business Name', placeholder: 'The Bow Tie Guy', type: 'text', testId: 'submit-business-input' },
];

export const ClientSubmitForm = ({ selections }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', business_name: '', notes: '' });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Please enter your name and email');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append('selections', JSON.stringify(selections));
      files.forEach((f) => fd.append('files', f));
      const res = await fetch(`${API_URL}/api/design-submissions`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error('Submission failed');
      setSubmitted(true);
      toast.success('Your selections have been sent to your designer!');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div id="submit-form" data-testid="submission-success" className="mt-8 bg-white rounded-lg shadow-md p-10 text-center">
        <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto mb-4" />
        <h3 className="text-2xl font-serif font-bold text-[#001B2A] mb-2">Selections Sent!</h3>
        <p className="text-gray-600 max-w-md mx-auto">
          Thank you, {form.name}! Your design selections, information, and files have been sent to your web designer. They'll be in touch soon.
        </p>
      </div>
    );
  }

  return (
    <div id="submit-form" className="mt-8 bg-white rounded-lg shadow-md p-6 sm:p-8">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-12 h-12 bg-[#E6D7B3] rounded-lg flex items-center justify-center">
          <Send className="h-6 w-6 text-[#001B2A]" />
        </div>
        <div>
          <h3 className="text-xl font-serif font-bold text-[#001B2A]">Submit Your Selections</h3>
          <p className="text-sm text-gray-600">Fill in your details, attach any files, and send everything to your designer in one click</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="mt-6 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          {CONTACT_FIELDS.map((field) => (
            <div key={field.name}>
              <label className="block text-sm font-semibold text-gray-700 mb-1">{field.label}</label>
              <input
                data-testid={field.testId}
                name={field.name}
                type={field.type}
                value={form[field.name]}
                onChange={handleChange}
                placeholder={field.placeholder}
                className={inputCls}
              />
            </div>
          ))}
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Notes / Comments</label>
          <textarea
            data-testid="submit-notes-input"
            name="notes"
            value={form.notes}
            onChange={handleChange}
            rows={4}
            placeholder="Anything else you'd like your designer to know — special requests, ideas, changes..."
            className={`${inputCls} resize-none`}
          />
        </div>

        <FileUploadField files={files} setFiles={setFiles} />

        <button
          type="submit"
          data-testid="submit-selections-button"
          disabled={submitting}
          className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3 bg-[#7B1E2B] text-white rounded-md font-semibold hover:bg-[#001B2A] transition-colors disabled:opacity-60"
        >
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
          {submitting ? 'Sending...' : 'Send to My Designer'}
        </button>
      </form>
    </div>
  );
};
